import React from "react";
import {
  fmtUptime,
  fmtNet,
  fmtGB,
  svStatus,
} from "../../utils/format";

// SVG Gauge Ring hiện đại theo thiết kế Argus
function SvgGaugeRing({ value, label, color, status }) {
  const isOffline = status === "offline";
  const displayVal = isOffline ? "—" : `${Math.round(value)}%`;
  const strokeVal = isOffline ? 0 : Math.round(value);

  return (
    <div style={{ position: "relative", width: "70px", height: "70px", flexShrink: 0 }}>
      <svg width="70" height="70" viewBox="0 0 70 70">
        <circle
          cx="35"
          cy="35"
          r="29"
          fill="none"
          stroke="var(--ring-track)"
          strokeWidth="6"
        ></circle>
        <circle
          cx="35"
          cy="35"
          r="29"
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray={`${strokeVal} 100`}
          transform="rotate(-90 35 35)"
          style={{ transition: "stroke-dasharray 0.4s ease, stroke 0.4s ease" }}
        ></circle>
      </svg>
      <div style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}>
        <span style={{
          fontFamily: "var(--font-mono)",
          fontSize: "14px",
          fontWeight: 700,
          color: isOffline ? "var(--faint)" : "var(--text)",
        }}>{displayVal}</span>
        <span style={{
          fontSize: "8.5px",
          fontWeight: 700,
          letterSpacing: ".5px",
          color: "var(--faint)",
          marginTop: "2px",
        }}>{label}</span>
      </div>
    </div>
  );
}

export default function ServerCard({ sv, canEdit, onEdit, onDelete }) {
  const code = sv.ServerCode;
  const m = sv.metrics || {};
  const cpu = m.cpuUsage || 0;
  const ram = m.ramPct || 0;
  const disk = m.diskPct || 0;
  const st = svStatus(sv); // online | warn | offline

  // Xác định màu sắc động dựa theo % sử dụng
  const getStatColor = (val) => {
    if (st === "offline") return "var(--faint)";
    if (val >= 85) return "var(--red)";
    if (val >= 70) return "var(--amber)";
    return "var(--green)";
  };

  const getDiskColor = (val) => {
    if (st === "offline") return "var(--faint)";
    if (val >= 90) return "var(--red)";
    if (val >= 75) return "var(--amber)";
    return "var(--green)";
  };

  const cpuColor = getStatColor(cpu);
  const ramColor = getStatColor(ram);
  const diskColor = getDiskColor(disk);

  // Xác định icon và màu sắc cho status badge ở header
  const statusColor = st === "offline" ? "var(--faint)" : st === "warn" ? "var(--amber)" : "var(--green)";
  const statusSoftBg = st === "offline" ? "rgba(93,102,120,.14)" : st === "warn" ? "var(--amber-soft)" : "var(--green-soft)";
  const statusShadow = st === "offline" ? "none" : `0 0 0 4px ${statusSoftBg}`;

  const roleText = sv.ServerName && sv.ServerName !== code ? sv.ServerName : "Monitor Agent";
  const ipAddress = sv.IP || "127.0.0.1";
  
  // CPU temperature formatting
  const cpuTempText = m.cpuTemp != null ? `${m.cpuTemp}°C` : "—";
  const cpuTempColor = st === "offline" ? "var(--faint)" : m.cpuTemp >= 75 ? "var(--red)" : m.cpuTemp >= 60 ? "var(--amber)" : "var(--green)";

  // Format net download / upload
  const rxSpeed = st === "offline" ? "↓0.0 B" : `↓${fmtNet(m.netRx)}`;
  const txSpeed = st === "offline" ? "↑0.0 B" : `↑${fmtNet(m.netTx)}`;

  return (
    <div 
      style={{
        borderRadius: "16px",
        background: "var(--surface)",
        border: `1px solid ${st === "warn" ? "var(--amber-soft)" : "var(--border)"}`,
        overflow: "hidden",
        boxShadow: "var(--shadow)",
        position: "relative",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        opacity: st === "offline" ? 0.72 : 1,
      }}
      className="server-detail-card"
    >
      {/* Top Warning Strip */}
      {st === "warn" && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: "linear-gradient(90deg, var(--amber), transparent 75%)",
        }}></div>
      )}

      {/* Card Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "15px 16px 14px",
        borderBottom: "1px solid var(--border)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "11px", minWidth: 0 }}>
          <span style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: statusColor,
            boxShadow: statusShadow,
            flexShrink: 0,
            animation: st === "warn" ? "warnBlink 2s infinite" : "none",
          }}></span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--text)", letterSpacing: "-.2px" }}>{code}</div>
            <div style={{ fontSize: "11.5px", color: "var(--faint)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {ipAddress} · {roleText}
            </div>
          </div>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <span style={{
            padding: "4px 11px",
            borderRadius: "99px",
            fontSize: "10.5px",
            fontWeight: 700,
            letterSpacing: ".5px",
            background: statusSoftBg,
            color: statusColor,
          }}>
            {st.toUpperCase()}
          </span>
          
          {canEdit && (
            <>
              <button
                onClick={() => onEdit(code)}
                title="Sửa"
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  background: "transparent",
                  border: "1px solid var(--border2)",
                  color: "var(--dim)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  transition: "all 0.2s ease",
                }}
                className="card-action-btn"
              >
                ✎
              </button>
              <button
                onClick={() => onDelete(code)}
                title="Xoá"
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  background: "transparent",
                  border: "1px solid var(--border2)",
                  color: "var(--dim)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  transition: "all 0.2s ease",
                }}
                className="card-action-btn-danger"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
        
        {/* Gauge Rings Grid */}
        <div style={{ display: "flex", gap: "14px" }}>
          {/* CPU Stats */}
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px" }}>
            <SvgGaugeRing value={cpu} label="CPU" color={cpuColor} status={st} />
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "11.5px" }}>
                <span style={{ color: "var(--faint)" }}>Temp</span>
                <span style={{ fontFamily: "var(--font-mono)", color: cpuTempColor, fontWeight: 600 }}>{cpuTempText}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "11.5px" }}>
                <span style={{ color: "var(--faint)" }}>Cores</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--dim)" }}>{m.cores || "—"}</span>
              </div>
            </div>
          </div>

          {/* RAM Stats */}
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px" }}>
            <SvgGaugeRing value={ram} label="RAM" color={ramColor} status={st} />
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "11.5px" }}>
                <span style={{ color: "var(--faint)" }}>Used</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--dim)", fontWeight: 600 }}>
                  {m.ramUsed != null ? fmtGB(m.ramUsed) : "—"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "11.5px" }}>
                <span style={{ color: "var(--faint)" }}>Total</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--dim)" }}>
                  {m.ramTotal ? `${Math.round(m.ramTotal)} GB` : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Disk Progress Bar */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".5px", color: "var(--faint)" }}>
              DISK · {m.diskUsed != null ? fmtGB(m.diskUsed) : "—"} / {m.diskTotal != null ? fmtGB(m.diskTotal) : "—"}
            </span>
            <span style={{ fontSize: "11.5px", fontFamily: "var(--font-mono)", fontWeight: 600, color: diskColor }}>
              {st === "offline" ? "—" : `${Math.round(disk)}%`}
            </span>
          </div>
          <div style={{ height: "6px", borderRadius: "99px", background: "var(--ring-track)", overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: st === "offline" ? 0 : `${Math.min(100, Math.max(0, disk))}%`,
              borderRadius: "99px",
              background: diskColor,
              transition: "width 0.4s ease, background 0.4s ease",
            }}></div>
          </div>
        </div>

        {/* Bottom Details Grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "1px",
          borderRadius: "11px",
          overflow: "hidden",
          border: "1px solid var(--border)",
          background: "var(--border)",
        }}>
          {/* Uptime */}
          <div style={{ background: "var(--surface2)", padding: "9px 11px" }}>
            <div style={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "3px" }}>Uptime</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "12px", fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {st === "offline" ? "—" : fmtUptime(m.uptime)}
            </div>
          </div>

          {/* Network speeds */}
          <div style={{ background: "var(--surface2)", padding: "9px 11px" }}>
            <div style={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "3px" }}>Net Speed</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "1px", fontFamily: "var(--font-mono)", fontSize: "10px", fontWeight: 600 }}>
              <span style={{ color: "var(--blue)" }}>{rxSpeed}</span>
              <span style={{ color: "var(--green)" }}>{txSpeed}</span>
            </div>
          </div>

          {/* Apps count */}
          <div style={{ background: "var(--surface2)", padding: "9px 11px" }}>
            <div style={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "3px" }}>Apps</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "12px", fontWeight: 600, color: "var(--text)" }}>
              {sv.apps?.length || 0}
            </div>
          </div>

          {/* Region */}
          <div style={{ background: "var(--surface2)", padding: "9px 11px" }}>
            <div style={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "3px" }}>Region</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "12px", fontWeight: 600, color: "var(--dim)" }}>
              {sv.Region || "SGN"}
            </div>
          </div>
        </div>
      </div>
      
      {/* Styles for hover and transition */}
      <style>{`
        .server-detail-card:hover {
          border-color: var(--border2) !important;
        }
        .card-action-btn:hover {
          color: var(--text) !important;
          border-color: var(--dim) !important;
        }
        .card-action-btn-danger:hover {
          color: var(--red) !important;
          border-color: var(--red) !important;
        }
      `}</style>
    </div>
  );
}
